/*
  WelcomeKits — keeps welcome-kit lines in step with the rungs that earn them,
  on every page.

  A prepaid rung (3-Month, 6-Month) ships a free kit on its first order. The
  kit is its own $0.00 cart line with no selling plan, so nothing in Shopify
  ties it to the rung: remove the rung and the kit stays, and checkout accepts
  a kit on its own as a whole $0.00 order (#86, #134).

  This used to live in subscription-panel.js, which only hero PDPs load, so a
  cart edited from the homepage, a collection, a single-herb PDP or /cart was
  never corrected (#134). It now loads from layout/theme.liquid and runs on
  every page. The PDP panel calls into it rather than keeping a copy.

  ONE SOURCE OF TRUTH. The rung-to-kit map, the household rule and the kit
  line property come from theme settings, rendered by
  snippets/welcome-kit-config.liquid into #WelcomeKitConfig. Nothing else in
  the theme knows which kit belongs to which rung. The Liquid checkout guard
  (snippets/welcome-kit-orphaned.liquid) reads the same settings.

  THE QUANTITY RULE, unchanged from #86: a rung line earns
  (kits per unit) x (line quantity) of the kit for its Supply value, where
  kits per unit is 2 for a two-person household and 1 otherwise. Only lines
  with a selling plan count, so a one-time hero (a Rebuy cross-sell add, the
  one-time row on the PDP) earns nothing, and kit lines, which never carry a
  plan, never count themselves.

  WHAT TRIGGERS IT. Every PUB_SUB_EVENTS.cartUpdate, whatever published it:
  the drawer and /cart (cart.js), the product form, the Rebuy cross-sell
  bridge (rebuy-cart-upsell.js), Standard Actions (refreshDawnCartUI). The PDP
  panel also calls reconcile() directly at submit time, with the rung it is
  about to add, so the kit lands before Dawn renders the drawer.
*/

(function () {
  if (window.WelcomeKits) return;

  const SOURCE = 'welcome-kits';
  // See onCartUpdate(): a write that does not take must not loop forever.
  const MAX_PASSES = 3;

  let config = null;
  let chain = Promise.resolve();
  let armed = false;
  let pending = false;
  /*
    An add in flight (#256). The kit goes into the cart first and the rung
    that earns it a moment later; a cart-change pass that reads the cart in
    between sees a kit nothing earns and removes it. While an add is in
    flight, passes may add kits but never take them away. It ends when the
    product form publishes its result (or an error), or after HOLD_MS as a
    backstop, and a full pass then runs against the live cart.
  */
  const HOLD_MS = 15000;
  let holding = false;
  let holdTimer = 0;

  function readConfig() {
    if (config) return config;
    const empty = { kits: {}, twoPerson: [], property: 'Welcome kit', value: 'ships once' };
    const el = document.getElementById('WelcomeKitConfig');
    try {
      const parsed = el ? JSON.parse(el.textContent) : {};
      config = Object.assign(empty, parsed || {});
    } catch (e) {
      config = empty;
    }
    // Variant ids as strings throughout, to compare with cart lines safely.
    Object.keys(config.kits).forEach((supply) => {
      config.kits[supply] = String(config.kits[supply]).trim();
      if (!config.kits[supply]) delete config.kits[supply];
    });
    return config;
  }

  function kitIds() {
    return new Set(Object.values(readConfig().kits));
  }

  function optionValues(item) {
    return (item.options_with_values || []).map((o) => o && o.value).filter(Boolean);
  }

  /*
    Kits one unit of a rung earns, from its option values. Matches on VALUE,
    not option name, so the rule needs nothing product-specific: the hero
    products all carry Supply = "3-Month Supply" etc. and Household =
    "Just Me" / "Two of Us".
  */
  function kitsPerUnit(values) {
    const { twoPerson } = readConfig();
    return values.some((v) => twoPerson.indexOf(v) !== -1) ? 2 : 1;
  }

  function supplyOf(values) {
    const { kits } = readConfig();
    return values.find((v) => Object.prototype.hasOwnProperty.call(kits, v)) || null;
  }

  /*
    The kit a rung with these option values earns per unit, or null.
    Used by the PDP panel for the rung it is about to add.
  */
  function kitFor(values) {
    const supply = supplyOf(values);
    if (!supply) return null;
    return { supply, id: readConfig().kits[supply], quantity: kitsPerUnit(values) };
  }

  function owedKits(cart) {
    const owed = {};
    (cart.items || []).forEach((item) => {
      if (!item.selling_plan_allocation) return;
      const kit = kitFor(optionValues(item));
      if (!kit) return;
      owed[kit.supply] = (owed[kit.supply] || 0) + kit.quantity * (item.quantity || 1);
    });
    return owed;
  }

  /*
    The writes that bring the cart's kit lines to what it owes, without
    making them. Pure, so a cart that is already right costs no request.

    A kit variant can sit on more than one line when properties differ, so
    the first line carries the whole corrected quantity and the rest go to
    zero rather than spreading a total across lines we did not write.
  */
  function plan(cart, extra) {
    const { kits } = readConfig();
    const owed = owedKits(cart);
    if (extra && extra.supply) owed[extra.supply] = (owed[extra.supply] || 0) + extra.quantity;

    const ids = kitIds();
    const lines = new Map();
    (cart.items || []).forEach((item) => {
      const id = String(item.variant_id);
      if (!ids.has(id)) return;
      if (!lines.has(id)) lines.set(id, []);
      lines.get(id).push({ key: item.key, quantity: item.quantity || 0 });
    });

    const writes = [];
    Object.entries(kits).forEach(([supply, id]) => {
      const want = owed[supply] || 0;
      const existing = lines.get(id) || [];
      if (!existing.length) {
        if (want > 0) writes.push({ add: id, quantity: want });
        return;
      }
      existing.forEach((line, i) => {
        const target = i === 0 ? want : 0;
        if (line.quantity !== target) writes.push({ key: line.key, quantity: target });
      });
    });
    return writes;
  }

  function isWholeCart(data) {
    return !!data && Array.isArray(data.items) && typeof data.item_count === 'number';
  }

  function fetchCart() {
    const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
    return fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((res) => res.json());
  }

  async function write(step) {
    const { property, value } = readConfig();
    const headers = { 'Content-Type': 'application/json', Accept: 'application/json' };
    if (step.add) {
      const addUrl = `${(window.routes && window.routes.cart_add_url) || '/cart/add'}.js`;
      return fetch(addUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          items: [{ id: Number(step.add), quantity: step.quantity, properties: { [property]: value } }],
        }),
      });
    }
    // Line keys stay valid as other lines change, so these run in sequence
    // without re-reading the cart between each one.
    const changeUrl = `${(window.routes && window.routes.cart_change_url) || '/cart/change'}.js`;
    return fetch(changeUrl, {
      method: 'POST',
      headers,
      body: JSON.stringify({ id: step.key, quantity: step.quantity }),
    });
  }

  /*
    Freeze the rendered cart while we write. Dawn addresses lines by INDEX
    (cart.js updateQuantity(line, …)), and removing a kit line shifts every
    index after it, so a click landing between our write and the repaint
    would change the wrong line. Same class Dawn uses for its own writes.
  */
  function setBusy(busy) {
    document.querySelectorAll('#main-cart-items, #CartDrawer-CartItems').forEach((el) => {
      el.classList.toggle('cart__items--disabled', busy);
    });
  }

  /*
    Bring the cart's kits to what it owes. `extra` is a rung about to be
    added ({ supply, quantity }), which is not in the cart yet. `cartData`
    is a whole cart the caller already holds; without it the cart is read.
    Returns how many writes were made.
  */
  // While an add is in flight, only writes that raise a kit line survive.
  function allowed(writes, cart) {
    if (!holding) return writes;
    const current = new Map((cart.items || []).map((item) => [item.key, item.quantity || 0]));
    return writes.filter((step) => step.add || step.quantity > (current.get(step.key) || 0));
  }

  async function reconcile(extra, cartData) {
    let cart = isWholeCart(cartData) ? cartData : null;
    // Known-good data can only prove there is nothing to do. If there is
    // work, re-read, so the writes are planned against the live cart.
    if (cart && allowed(plan(cart, extra), cart).length === 0) return 0;
    cart = await fetchCart();
    const writes = allowed(plan(cart, extra), cart);
    if (!writes.length) return 0;

    setBusy(true);
    for (const step of writes) await write(step);
    return writes.length;
  }

  /*
    One reconciliation at a time, and never a thrown one. A kit that fails to
    reconcile must never break the cart: a miscounted gift is a fulfilment
    note, a broken drawer is a lost order. The Liquid guard in the drawer and
    on /cart still holds checkout on a kit-only cart if this fails.
  */
  function queue(task) {
    const run = chain.then(async () => {
      try {
        return await task();
      } catch (e) {
        // Deliberately swallowed; see above.
      }
    });
    chain = run;
    return run;
  }

  /*
    Repaint every cart surface after a corrective write, so the shopper never
    sees a stale $0.00 kit line. refreshDawnCartUI() (standard-actions-
    override.js) repaints the sections Dawn's subscribers leave alone, the
    cart badge among them, then publishes cart-update so CartItems re-renders
    the drawer and /cart. That publish comes back here as one confirming
    pass, which finds nothing to do and costs no request (it carries the
    whole cart). It resolves once those subscribers have repainted. The
    fallback publishes directly under our own source, which onCartUpdate()
    ignores.
  */
  async function refresh() {
    if (typeof refreshDawnCartUI === 'function') return refreshDawnCartUI();
    if (typeof publish !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    const cart = await fetchCart();
    // cartData is not optional: quick-add-bulk.js reads cartData.items unchecked.
    return publish(PUB_SUB_EVENTS.cartUpdate, { source: SOURCE, cartData: cart });
  }

  /*
    Never two cart-change passes at once. The flag goes up HERE, not inside
    the pass, because a burst of mutations lands in one turn and the pass does
    not start until later: three rapid removals fold into one follow-up
    rather than three passes each re-reading a cart the one before fixed.

    Bounded, deliberately. A kit that cannot be written (out of stock,
    unpublished) would otherwise write, refresh and re-arm forever. Three
    passes, then leave it alone; the Liquid guard still holds checkout.

    Returns nothing, so a publisher awaiting Promise.all over its subscribers
    (product-form.js does) is never held up by our network calls.
  */
  function release() {
    holding = false;
    clearTimeout(holdTimer);
  }

  function onCartUpdate(event) {
    if (event && event.source === SOURCE) return;
    if (!Object.keys(readConfig().kits).length) return;
    // The add has landed (or failed): the cart is whole again, so this pass
    // may remove what nothing earns.
    if (event && event.source === 'product-form') release();

    if (armed) {
      pending = true;
      return;
    }
    armed = true;
    let latest = event && event.cartData;

    queue(async () => {
      try {
        for (let pass = 0; pass < MAX_PASSES; pass += 1) {
          pending = false;
          try {
            const writes = await reconcile(null, latest);
            // Waits for the drawer / cart page to repaint (see refresh()),
            // so the rows stay frozen until their indices are true again.
            if (writes > 0) await refresh();
          } finally {
            setBusy(false);
          }
          latest = null; // a follow-up pass must read the live cart
          if (!pending) break;
        }
      } finally {
        armed = false;
        pending = false;
      }
    });
  }

  window.WelcomeKits = {
    kitFor,
    owedKits,
    plan,
    // The panel's submit path: queued on the same chain as cart-change
    // passes so the two never interleave. `extra` is the rung about to be
    // added; passing one starts the hold described at HOLD_MS.
    reconcile: (extra) =>
      queue(async () => {
        if (extra) {
          holding = true;
          clearTimeout(holdTimer);
          holdTimer = setTimeout(() => {
            release();
            onCartUpdate({ source: 'hold-expired' });
          }, HOLD_MS);
        }
        try {
          return await reconcile(extra);
        } finally {
          setBusy(false);
        }
      }),
    /*
      Resolves once every queued pass has run and one more full pass against
      the live cart has made it right. For a caller about to leave the page
      (the sticky Check Out), which must not navigate while a kit write is
      still owed.
    */
    settle: () => {
      release();
      return queue(async () => {
        try {
          return await reconcile(null);
        } finally {
          setBusy(false);
        }
      });
    },
  };

  function onCartError() {
    release();
    onCartUpdate({ source: 'cart-error' });
  }

  function init() {
    if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    subscribe(PUB_SUB_EVENTS.cartUpdate, onCartUpdate);
    if (PUB_SUB_EVENTS.cartError) subscribe(PUB_SUB_EVENTS.cartError, onCartError);

    /*
      Catch a cart that is ALREADY wrong when the page loads: a kit orphaned
      in another tab, before this shipped, or by a write that failed. Only
      when the cart is non-empty, so an empty session costs no request.
    */
    const bubble = document.getElementById('cart-icon-bubble');
    const count = bubble && bubble.querySelector('.cart-count-bubble');
    if (count || document.getElementById('main-cart-items')) onCartUpdate({ source: 'page-load' });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }
})();
